<script lang="ts" context="module">
  import type { Load } from '@sveltejs/kit';
  import { getUserStore } from '$lib/stores/getUserStore';
  import type { UserCheckpointStore } from 'shared/src/user/userLessons/user.checkpoint.store';

  export const load: Load = async ({ session, params, fetch }) => {
    const clientUserStore = await getUserStore({ userWithJoins: session.user, fetch });

    const checkpoint = clientUserStore.lessonProgress.lessonsById[params.checkpointId!];

    // If the checkpoint doesn't exist, 404!
    if (!checkpoint) {
      return {
        status: 404,
      };
    }

    // If the lesson isn't available, redirect to the base lesson
    // page to let them know
    if (!checkpoint.isVisible || !checkpoint.isAvailable) {
      return {
        status: 303,
        redirect: `/app/lessons/${params.lessonId}`,
      };
    }

    // If it's a lesson, go to the right page:
    if (checkpoint.type === 'lesson') {
      return {
        status: 303,
        redirect: `/app/lessons/${params.lessonId}`,
      };
    }

    // All is well, let's run the test:
    return {
      props: {
        checkpoint,
        clientUserStore,
      },
    };
  };
</script>

<script lang="ts">
  import CheckpointResultsComponent from '$lib/app/tests/CheckpointResults.svelte';
  import CheckpointTestRunner from '$lib/app/tests/CheckpointTestRunner.svelte';
  import type {
    CheckpointResults,
    LessonCheckpointTest,
  } from 'shared/definitions/checkpoint-definitions';
  import type { ClientUserStore } from 'shared/src';
  import type { TestAttemptStore } from 'shared/src/user/tests/checkpoint.attempt.store';
  import H from '$lib/components/H.svelte';
  import { autorun } from 'mobx';
  import { onDestroy } from 'svelte';
  import Button from '$lib/components/Button.svelte';
  import NextLessonButton from '$lib/app/lessons/NextLessonButton.svelte';
  import { goto } from '$app/navigation';
  import PageTitle from '$lib/teach/resources/PageTitle.svelte';
  import { titleCase } from 'shared/src/utils/titleCase';

  export let clientUserStore: ClientUserStore;
  export let checkpoint: UserCheckpointStore;
  const test = checkpoint.model.definition as LessonCheckpointTest;

  let state: 'noStore' | 'runningTest' | 'showingResults' | 'testErrored' | 'resultsErrored' =
    'runningTest';

  // Get the checkpoint store:
  const checkpointStore = clientUserStore.lessonProgress.lessonsById[test.id] as
    | UserCheckpointStore
    | undefined;

  if (!checkpointStore) state = 'noStore';

  let nextLessonIsAvailable = false;
  let hasAttemptsRemaining = false;
  let checkpointTitle: string;
  const destroy = autorun(() => {
    nextLessonIsAvailable = !!checkpointStore?.nextLesson?.isAvailable;
    hasAttemptsRemaining = !!checkpointStore?.hasAttemptsRemaining;
    checkpointTitle = titleCase(checkpointStore?.title || '');
  });
  onDestroy(destroy);

  let results: TestAttemptStore | undefined;
  function testEnded(data: CustomEvent<CheckpointResults>) {
    // Get the present attempt:
    results = checkpointStore?.attempts.find((attempt) => attempt.id === data.detail.id);
    state = 'showingResults';
    if (!results) {
      state = 'resultsErrored';
    }
  }

  function repeatTest() {
    results = undefined;
    state = 'runningTest';
  }
</script>

<PageTitle
  title={checkpointTitle + ' Checkpoint'}
  description="uTheory checkpoint test {checkpointStore ? `on ${checkpointStore.title}` : ''}"
  class="sr-only"
/>
<div class="card sm:m-4 p-2">
  {#if state === 'noStore'}
    It appears that checkpoint is not available to you at this time. If you believe this is an
    error, please contact your teacher or write to us at support@utheory.com.
  {:else if state === 'runningTest'}
    <CheckpointTestRunner
      {test}
      {clientUserStore}
      on:testEnded={(data) => {
        // @ts-ignore
        testEnded(data);
      }}
    />
  {:else if state === 'showingResults'}
    {#if results}
      <H class="text-center">{checkpointTitle} Results</H>
      <CheckpointResultsComponent class="p-2" attempt={results} {clientUserStore} />
    {:else}
      Sorry, something went wrong and we aren't able to show you your checkpoint results at the
      moment. Try going back to the lessons page and clicking on the results by the name of the
      checkpoint.
    {/if}
    <div class="flex justify-center space-x-4">
      <!-- Repeat, if available -->
      {#if hasAttemptsRemaining}
        <Button on:click={repeatTest}>Repeat</Button>
      {/if}
      <!-- Next lesson if available -->
      {#if nextLessonIsAvailable && checkpointStore}
        <NextLessonButton lesson={checkpointStore} />
      {:else}
        <!-- Lessons list, otherwise -->
        <Button on:click={() => goto('/app/lessons')}>Lessons</Button>
      {/if}
    </div>
  {:else if state == 'resultsErrored'}
    Sorry, something went wrong and we aren't able to show you your checkpoint results at the
    moment. Try going back to the lessons page and clicking on the results by the name of the
    checkpoint.
  {:else if state === 'testErrored'}
    Sorry, we weren't able to start the checkpoint. If this error continues please contact us at
    support@utheory.com.
  {/if}
</div>
